/**
 * Maps the internal wizard payload to the API request shape.
 *
 * The wizard stores form-friendly field names (interviewName, interview_type, bookedSlot, etc.).
 * The API expects different field names (name, kind, start_time, end_time, etc.).
 * This module bridges the gap so the rest of the code stays clean.
 */
import type { ScheduleInterviewPayload, ScheduleMode } from '../schedule.types';

// Fields that are UI-only and should not be sent to the API
const UI_ONLY_FIELDS = [
  'dateTime',
  'findTime',
  'book_meeting_room',
  'candidatePhone',
  'bookedSlot',
  'selfScheduleSlots',
] as const;

interface ContextIds {
  ats_application_id?: number;
  ats_job_id?: number;
  ats_job_stage_id?: number;
  ats_job_stage_action_id?: number | null;
  candidate_id?: number;
}

interface BookedSlot {
  start: string;
  end: string;
  duration?: number;
}

interface ScheduleSlot {
  start: string;
  end: string;
  duration?: number;
  is_custom?: boolean;
  override_type?: string;
}

function stripUiOnlyFields(
  obj: Record<string, unknown>
): Record<string, unknown> {
  const cleaned = { ...obj };
  for (const key of UI_ONLY_FIELDS) {
    delete cleaned[key];
  }
  return cleaned;
}

/**
 * Map details form values → API-compatible fields.
 */
function mapDetailsToApi(
  details: Record<string, unknown>,
  mode: ScheduleMode
): Record<string, unknown> {
  const isSelfSchedule =
    mode === 'self-schedule' || mode === 'self-schedule-adhoc';

  const mapped: Record<string, unknown> = {};

  // Interview name: form uses `interviewName`, API uses `name`
  if ('interviewName' in details) {
    mapped.name = details.interviewName;
  }

  // Interview type: form uses `interview_type`, API uses `kind`
  if ('interview_type' in details) {
    mapped.kind = details.interview_type;
  }

  // Participant IDs: always send as number array
  if ('participant_ids' in details) {
    const raw = details.participant_ids;
    if (Array.isArray(raw)) {
      mapped.participant_ids = raw.map(Number);
    } else if (raw) {
      mapped.participant_ids = [Number(raw)];
    } else {
      mapped.participant_ids = [];
    }
  }

  // Duration
  if ('duration' in details) {
    mapped.duration = Number(details.duration) || 30;
  }

  // Location
  if ('location' in details) {
    mapped.location = details.location;
  }

  // Host ID (Zoom only)
  if (details.host_id) {
    mapped.host_id = Number(details.host_id);
  }

  // Office ID (Onsite only)
  if (details.ats_office_id) {
    mapped.ats_office_id = Number(details.ats_office_id);
  }

  // Calendar visibility: form stores boolean, API expects string
  if ('calendar_visibility' in details) {
    mapped.calendar_visibility = details.calendar_visibility
      ? 'private'
      : 'default';
  }

  // Note taker
  if ('note_taker_enabled' in details) {
    mapped.note_taker_enabled = Boolean(details.note_taker_enabled);
  }

  // Fill with AI
  if ('fill_with_ai_enabled' in details) {
    mapped.fill_with_ai_enabled = Boolean(details.fill_with_ai_enabled);
  }

  // Calendar info — only when plan allows shared calendars (flag synced in DetailsStep)
  const sharedCalEnabled = Boolean(details.shared_calendar_enabled);
  const rawCal = details.calendar_info as
    | { provider_calendar_id?: string }
    | null
    | undefined;
  const hasCalendarInfo =
    sharedCalEnabled &&
    rawCal &&
    typeof rawCal === 'object' &&
    typeof rawCal.provider_calendar_id === 'string' &&
    rawCal.provider_calendar_id.length > 0;

  if (hasCalendarInfo) {
    mapped.calendar_info = details.calendar_info;
  }

  // Meeting rooms: map email IDs to meeting_rooms_attributes format
  if ('meeting_room_ids' in details) {
    const roomIds = details.meeting_room_ids;
    if (Array.isArray(roomIds) && roomIds.length > 0) {
      mapped.meeting_rooms_attributes = roomIds.map((email: string) => ({
        email,
        name: email,
      }));
    }
  }

  // Timezone from shared state (stored in details by DetailsStep.syncToPayload)
  if ('timezone' in details) {
    mapped.timezone = details.timezone;
  }

  // Manual scheduling: extract start_time/end_time from bookedSlot
  if (!isSelfSchedule) {
    const bookedSlot = details.bookedSlot as BookedSlot | null | undefined;
    if (bookedSlot?.start && bookedSlot?.end) {
      mapped.start_time = bookedSlot.start;
      mapped.end_time = bookedSlot.end;
    }
  }

  // Self-schedule: extract time_slots from selfScheduleSlots
  if (isSelfSchedule) {
    const slots = details.selfScheduleSlots as ScheduleSlot[] | undefined;
    if (Array.isArray(slots) && slots.length > 0) {
      mapped.time_slots = slots.map((slot) => ({
        start_at: slot.start,
        end_at: slot.end,
        is_custom: slot.is_custom ?? false,
        ...(slot.override_type ? { override_type: slot.override_type } : {}),
      }));
    }
  }

  return mapped;
}

/**
 * Build the final API payload from the wizard's internal payload shape.
 */
export function buildSchedulePayload(
  payload: ScheduleInterviewPayload,
  contextIds: ContextIds,
  mode: ScheduleMode
): Record<string, unknown> {
  const details = stripUiOnlyFields(payload.details);
  const mappedDetails = mapDetailsToApi(details, mode);

  return {
    ...contextIds,
    ...mappedDetails,
    ...payload.communication,
    ...payload.scorecard,
    ...(payload.slotSelectionEmail ?? {}),
  };
}
